import { useState, useRef, useEffect } from 'react';
import { AtSign } from 'lucide-react';
import annotationsService from '../../services/annotations';

/**
 * Composant Textarea avec autocomplete pour les mentions @username
 */
export default function MentionInput({ value, onChange, placeholder, rows = 3, className = '' }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Détecter la saisie de @ et déclencher l'autocomplete
  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    onChange(newValue);
    setCursorPosition(cursorPos);

    // Détecter si on vient de taper @
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Vérifier qu'il n'y a pas d'espace après le @
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      const hasSpace = textAfterAt.includes(' ');

      if (!hasSpace && textAfterAt.length >= 0) {
        setMentionQuery(textAfterAt);
        loadSuggestions(textAfterAt);
        setShowSuggestions(true);
        setSelectedIndex(0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  // Charger les suggestions depuis l'API
  const loadSuggestions = async (query) => {
    try {
      const results = await annotationsService.getMentionSuggestions(query);
      setSuggestions(results);
    } catch (error) {
      console.error('Erreur chargement suggestions:', error);
      setSuggestions([]);
    }
  };

  // Insérer une mention sélectionnée
  const insertMention = (user) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);

    // Trouver le début de la mention (@)
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    // Construire le nouveau texte avec la mention complète
    const newText =
      textBeforeCursor.substring(0, lastAtIndex) +
      `@${user.username} ` +
      textAfterCursor;

    onChange(newText);
    setShowSuggestions(false);

    // Replacer le curseur après la mention
    setTimeout(() => {
      const newCursorPos = lastAtIndex + user.username.length + 2; // +2 pour @ et espace
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Gestion du clavier (flèches haut/bas, Enter, Escape)
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
        break;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        break;

      case 'Enter':
        if (showSuggestions) {
          e.preventDefault();
          insertMention(suggestions[selectedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        break;

      default:
        break;
    }
  };

  // Fermer les suggestions si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Scroll automatique vers l'élément sélectionné
  useEffect(() => {
    if (suggestionsRef.current && showSuggestions) {
      const selectedElement = suggestionsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, showSuggestions]);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Tapez @ pour mentionner un utilisateur...'}
        rows={rows}
        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none ${className}`}
      />

      {/* Dropdown des suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50"
        >
          {suggestions.map((user, index) => (
            <div
              key={user.id}
              onClick={() => insertMention(user)}
              className={`flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors ${
                index === selectedIndex
                  ? 'bg-primary-100 text-primary-900'
                  : 'hover:bg-gray-100'
              }`}
            >
              <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                {user.displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.displayName}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  @{user.username}
                </p>
              </div>
              <AtSign className="w-4 h-4 text-gray-400" />
            </div>
          ))}
        </div>
      )}

      {/* Indication d'aide */}
      <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
        <AtSign className="w-3 h-3" />
        <span>Tapez @ pour mentionner un utilisateur</span>
      </div>
    </div>
  );
}
